// The board's own clipboard (qa-runs#1428): what the DM copied — the things' ids and where they stood — until the
// page is reloaded. Never the computer's clipboard. A paste is ONE `objects.paste` of at most `OBJECT_LIMITS.copies`
// (50, qa-runs#1446; the server holds the same cap), the group keeping its shape around the spot chosen, a copy whose
// square is taken going to the nearest free one (`placeSpot`), and the copies selected afterwards, ready to drag.
import { OBJECT_LIMITS, type EventBody, type Grid, type Point } from '@dragons-table/vtt-shared';
import { placeSpot } from '../place';
import { selection } from './selection';

interface Copied { scene: string | null; items: Array<{ source: string; x: number; y: number }> }
let copied: Copied | null = null;
const listeners = new Set<() => void>();

export const boardClipboard = {
  /** How many a paste would make, 0 when nothing is copied or what was copied is on another scene. */
  count(scene: string | null): number { return copied && copied.scene === scene ? Math.min(copied.items.length, OBJECT_LIMITS.copies) : 0; },
  copy(scene: string | null, things: Array<{ id: string; x: number; y: number }>): void {
    copied = things.length ? { scene, items: things.slice(0, OBJECT_LIMITS.copies).map((o) => ({ source: o.id, x: o.x, y: o.y })) } : null;
    for (const l of listeners) l();
  },
  subscribe(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** The paste at `at`, or null when there is nothing to paste here (a source since removed is left out). */
  paste(scene: string | null, at: Point, state: { grid: Grid; scene: { snap?: boolean }; objects: Record<string, { x: number; y: number }> }, extent: { width: number; height: number }): EventBody | null {
    if (!copied || copied.scene !== scene) return null;
    const items = copied.items.filter((i) => state.objects[i.source]);
    if (!items.length) return null;
    const first = items[0]!;
    const taken = Object.values(state.objects).map((o) => ({ x: o.x, y: o.y }));
    const out = items.map((i) => {
      const spot = placeSpot(state.grid, extent, { x: at.x + i.x - first.x, y: at.y + i.y - first.y }, state.scene.snap !== false, taken, true);
      taken.push(spot);
      return { source: i.source, id: crypto.randomUUID(), x: spot.x, y: spot.y };
    });
    selection.replace(out.map((i) => i.id));
    return { kind: 'objects.paste', items: out };
  },
};
